import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/firebase';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';
import Header from '../components/Header';

const GameRating = () => {
  const { gameId, hostId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [host, setHost] = useState(null);
  const [team, setTeam] = useState(null);
  const [hasRated, setHasRated] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  
  // Rating state
  const [ratings, setRatings] = useState({
    host: {
      rating: 0,
      comment: ''
    },
    game: {
      rating: 0,
      difficulty: 3,
      entertainment: 3,
      comment: ''
    }
  });

  const loadData = useCallback(async () => {
    try {
      // Load game data
      const gameDoc = await getDoc(doc(db, 'games', gameId));
      if (gameDoc.exists()) {
        setGame(gameDoc.data());
      }

      // Load host data
      const hostDoc = await getDoc(doc(db, 'users', hostId));
      if (hostDoc.exists()) {
        setHost(hostDoc.data());
      }

      // Load team data
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      
      if (userData?.teamId) {
        const teamDoc = await getDoc(doc(db, 'teams', userData.teamId));
        if (teamDoc.exists()) {
          setTeam(teamDoc.data());
        }
      }

      // Check if team has already rated this game
      const ratingQuery = query(
        collection(db, 'gameRatings'),
        where('gameId', '==', gameId),
        where('teamId', '==', userData?.teamId)
      );
      const ratingSnapshot = await getDocs(ratingQuery);
      
      if (!ratingSnapshot.empty) {
        setHasRated(true);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [gameId, hostId, user]);

  useEffect(() => {
    if (user && gameId && hostId) {
      loadData();
    }
  }, [user, gameId, hostId, loadData]);

  const handleRatingChange = (type, field, value) => {
    setRatings(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const submitRatings = async () => {
    if (!team || !game || !host) return;

    try {
      const ratingId = `rating_${Date.now()}`;
      const timestamp = new Date();

      // Submit host rating
      await setDoc(doc(db, 'hostRatings', ratingId), {
        id: ratingId,
        hostId: hostId,
        gameId: gameId,
        teamId: team.id,
        leagueId: team.leagueId,
        rating: ratings.host.rating,
        comment: ratings.host.comment,
        timestamp: timestamp
      });

      // Submit game rating
      await setDoc(doc(db, 'gameRatings', ratingId), {
        id: ratingId,
        gameId: gameId,
        hostId: hostId,
        teamId: team.id,
        leagueId: team.leagueId,
        rating: ratings.game.rating,
        difficulty: ratings.game.difficulty,
        entertainment: ratings.game.entertainment,
        comment: ratings.game.comment,
        timestamp: timestamp
      });

      // Update game's average ratings
      await updateGameRatings();
      
      setRatingSubmitted(true);
    } catch (error) {
      console.error('Error submitting ratings:', error);
    }
  };

  const updateGameRatings = async () => {
    try {
      // Get all game ratings
      const gameRatingsQuery = query(
        collection(db, 'gameRatings'),
        where('gameId', '==', gameId)
      );
      const gameRatingsSnapshot = await getDocs(gameRatingsQuery);
      
      let totalRating = 0;
      let totalDifficulty = 0;
      let totalEntertainment = 0;
      let count = 0;

      gameRatingsSnapshot.forEach(doc => {
        const data = doc.data();
        totalRating += data.rating;
        totalDifficulty += data.difficulty;
        totalEntertainment += data.entertainment;
        count++;
      });

      const avgRating = count > 0 ? totalRating / count : 0;
      const avgDifficulty = count > 0 ? totalDifficulty / count : 0;
      const avgEntertainment = count > 0 ? totalEntertainment / count : 0;

      // Update game document with new averages
      await updateDoc(doc(db, 'games', gameId), {
        averageRating: avgRating,
        averageDifficulty: avgDifficulty,
        averageEntertainment: avgEntertainment,
        ratingCount: count
      });

    } catch (error) {
      console.error('Error updating game ratings:', error);
    }
  };

  const renderStars = (rating, onRatingChange, type) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => onRatingChange(type, 'rating', star)}
            className={`text-2xl ${
              rating >= star ? 'text-yellow-400' : 'text-gray-400'
            } hover:text-yellow-300 transition-colors`}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  const renderSlider = (value, onValueChange, type, field, label) => {
    return (
      <div className="space-y-2">
        <label className="block text-white text-sm">{label}: {value}</label>
        <input
          type="range"
          min="1"
          max="5"
          value={value}
          onChange={(e) => onValueChange(type, field, parseInt(e.target.value))}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
        />
        <div className="flex justify-between text-xs text-gray-400">
          <span>1</span>
          <span>2</span>
          <span>3</span>
          <span>4</span>
          <span>5</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading rating form...</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">League Membership Required</h1>
          <p className="text-gray-400 mb-6">You need to be part of a trivia team to rate games and hosts.</p>
          <button
            onClick={() => navigate('/team-profile')}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Create or Join Team
          </button>
        </div>
      </div>
    );
  }

  if (hasRated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Already Rated!</h1>
          <p className="text-gray-400">Your team has already submitted ratings for this game.</p>
        </div>
      </div>
    );
  }

  if (ratingSubmitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Thank You!</h1>
          <p className="text-gray-400 mb-6">Your ratings have been submitted successfully.</p>
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <p className="text-gray-400 text-sm">Your feedback helps improve the trivia experience for everyone!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Logo className="h-16 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Rate Your Experience</h1>
            <p className="text-gray-400">Help improve trivia for everyone!</p>
          </div>

          {/* Game Info */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Game Details</h2>
            <div className="space-y-2 text-gray-300">
              <p><span className="font-semibold">Game:</span> {game?.title || 'Unknown Game'}</p>
              <p><span className="font-semibold">Host:</span> {host?.displayName || 'Unknown Host'}</p>
              <p><span className="font-semibold">Your Team:</span> {team.name}</p>
            </div>
          </div>

          {/* Rating Forms */}
          <div className="space-y-6">
            {/* Host Rating */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Rate the Host</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-white mb-2">Overall Rating</label>
                  {renderStars(ratings.host.rating, handleRatingChange, 'host')}
                </div>

                <div>
                  <label className="block text-white mb-2">Comments (Optional)</label>
                  <textarea
                    value={ratings.host.comment}
                    onChange={(e) => handleRatingChange('host', 'comment', e.target.value)}
                    className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                    rows="3"
                    placeholder="How was the host's performance, energy, and crowd engagement?"
                  />
                </div>
              </div>
            </div>

            {/* Game Rating */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Rate the Game</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-white mb-2">Overall Rating</label>
                  {renderStars(ratings.game.rating, handleRatingChange, 'game')}
                </div>

                {renderSlider(
                  ratings.game.difficulty,
                  handleRatingChange,
                  'game',
                  'difficulty',
                  'Difficulty Level'
                )}

                {renderSlider(
                  ratings.game.entertainment,
                  handleRatingChange,
                  'game',
                  'entertainment',
                  'Entertainment Value'
                )}

                <div>
                  <label className="block text-white mb-2">Comments (Optional)</label>
                  <textarea
                    value={ratings.game.comment}
                    onChange={(e) => handleRatingChange('game', 'comment', e.target.value)}
                    className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                    rows="3"
                    placeholder="How was the game content, questions, and overall experience?"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={submitRatings}
              disabled={ratings.host.rating === 0 || ratings.game.rating === 0}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-3 px-4 rounded transition duration-200"
            >
              Submit Ratings
            </button>

            <p className="text-gray-400 text-sm text-center">
              One rating per team. Your feedback helps improve the platform for everyone!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameRating; 